/**
 * The insights studio's way in (insights studio S2, docs/PLAN-insights-studio-20261016.md): the account menu offers
 * /admin/insights only in Prest0 Inc.'s workspace, and only to someone holding `insights.posts.write`.
 *
 * Both halves are needed. `hasPermission` answers true for the owner of ANY workspace (an owner holds every key of their
 * own), while the insights keys exist only in Prest0 Inc.: the server strips them everywhere else and refuses them
 * before its owner pass. The workspace is the ACTIVE one (a switch is a hard navigation), read from
 * src/services/activeWorkspace.ts, never the home org (`user.accountId` does not follow a switch).
 *
 * The permission answer comes from what the menu already reads (useUsageVisibility, which wraps useRbac), never from a
 * second useRbac here: that would bring Firebase Auth into every module that imports the menu's list, and the tests that
 * draw the header stand in for useUsageVisibility alone. A stand-in without `hasPermission` reads as "not offered".
 *
 * This hides chrome only. The route's DeveloperRoute and every callable decide (functions/src/insights/insightsAccess.ts).
 * It costs a customer no server call: the workspace id and the permission set are already in memory.
 */
import { useEffect, useState } from 'react';
import { PREST0_INC_ACCOUNT_ID } from '../config/internalAccess';
import { getActiveWorkspaceAccount, onActiveWorkspaceResolved } from '../services/activeWorkspace';

/** The key the studio's route asks for (src/App.tsx), and so the key its way in asks for. */
export const INSIGHTS_STUDIO_KEY = 'insights.posts.write';

/** The permission answer the rule needs (useUsageVisibility's `loading` and `hasPermission`). */
export interface InsightsStudioPermissions {
  loading?: boolean;
  hasPermission?: (key: string) => boolean;
}

/** May the way in show, in this workspace, for this permission answer? False while it loads (fail closed). Pure. */
export function canSeeInsightsStudio(activeAccountId: string | null, perms: InsightsStudioPermissions): boolean {
  if (perms.loading || typeof perms.hasPermission !== 'function') return false;
  return activeAccountId === PREST0_INC_ACCOUNT_ID && perms.hasPermission(INSIGHTS_STUDIO_KEY);
}

/** The active workspace's account id, filled in when it resolves after the first render (null until then). */
export function useActiveWorkspaceAccountId(): string | null {
  const [accountId, setAccountId] = useState<string | null>(() => getActiveWorkspaceAccount());
  useEffect(() => {
    const now = getActiveWorkspaceAccount();
    if (now) setAccountId(now);
    return onActiveWorkspaceResolved((acct) => setAccountId(acct));
  }, []);
  return accountId;
}
